import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Workflow, Loader2, Plus, Trash2, Play, Power, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TRIGGERS = ['application_status', 'compliance_status', 'onboarding_status', 'manual'];
const ACTIONS = ['notify', 'flag_for_review', 'add_note', 'none'];
const OPERATORS = ['eq', 'ne', 'in', 'contains', 'gt', 'lt'];

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const WorkflowAutomationPage = () => {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState({
    name: '', description: '', trigger_type: 'application_status',
    cond_field: 'status', cond_operator: 'eq', cond_value: '', action_type: 'flag_for_review',
  });
  const [evalResult, setEvalResult] = useState(null);
  const [evalContext, setEvalContext] = useState('{\n  "status": "rejected"\n}');
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/automation/rules`, { headers: authHeaders() });
      if (res.status === 403) { setAvailable(false); return; }
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      setRules(data.rules || []);
      setAvailable(true);
    } catch { /* keep prior */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createRule = async (e) => {
    e.preventDefault();
    setMsg('');
    const conditions = form.cond_field
      ? [{ field: form.cond_field, operator: form.cond_operator, value: form.cond_value }]
      : [];
    const res = await fetch(`${API_URL}/api/automation/rules`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({
        name: form.name, description: form.description, trigger_type: form.trigger_type,
        conditions, action: { type: form.action_type, params: {} },
      }),
    });
    if (res.status === 403) { setMsg('You do not have permission to create rules (Super Admin only).'); return; }
    if (!res.ok) { const d = await res.json().catch(() => ({})); setMsg(d.detail || 'Create failed'); return; }
    setForm({ ...form, name: '', description: '', cond_value: '' });
    setMsg('Rule created (disabled by default).');
    load();
  };

  const toggle = async (r) => {
    const res = await fetch(`${API_URL}/api/automation/rules/${r.id}/toggle`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ enabled: !r.enabled }),
    });
    if (res.status === 403) { setMsg('Toggle requires Super Admin.'); return; }
    load();
  };

  const remove = async (r) => {
    const res = await fetch(`${API_URL}/api/automation/rules/${r.id}`, { method: 'DELETE', headers: authHeaders() });
    if (res.status === 403) { setMsg('Delete requires Super Admin.'); return; }
    load();
  };

  const evaluate = async (r) => {
    setEvalResult(null); setMsg('');
    let ctx;
    try { ctx = JSON.parse(evalContext); } catch { setMsg('Context must be valid JSON.'); return; }
    const res = await fetch(`${API_URL}/api/automation/rules/${r.id}/evaluate`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ context: ctx }),
    });
    if (!res.ok) { setMsg('Evaluate failed'); return; }
    setEvalResult(await res.json());
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="workflow-automation-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Workflow className="w-7 h-7 text-indigo-600" />
            Workflow Automation
          </h1>
          <p className="text-gray-600 mt-1">
            Define automation rules and dry-run them safely. Human-in-the-loop: evaluation never executes an action.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="workflow-automation-loading">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : !available ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="workflow-automation-unavailable">
            <p className="text-gray-700">Workflow Automation is not currently available.</p>
          </div>
        ) : (
          <>
            {msg && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800" data-testid="workflow-automation-msg">
                {msg}
              </div>
            )}

            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm" data-testid="workflow-automation-hitl-banner">
              <ShieldAlert className="w-5 h-5" />
              Rules are disabled by default and run in manual (human-in-the-loop) mode. No automation executes without human action.
            </div>

            <form onSubmit={createRule} className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-testid="workflow-automation-create-form">
              <h2 className="font-semibold text-gray-900">Create Rule</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Rule name" data-testid="rule-name-input"
                  className="border rounded-lg px-3 py-2 text-sm" />
                <select value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}
                  data-testid="rule-trigger-select" className="border rounded-lg px-3 py-2 text-sm">
                  {TRIGGERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description (optional)" data-testid="rule-description-input"
                className="border rounded-lg px-3 py-2 text-sm w-full" />
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <input value={form.cond_field} onChange={(e) => setForm({ ...form, cond_field: e.target.value })}
                  placeholder="field" data-testid="rule-cond-field-input" className="border rounded-lg px-3 py-2 text-sm" />
                <select value={form.cond_operator} onChange={(e) => setForm({ ...form, cond_operator: e.target.value })}
                  data-testid="rule-cond-operator-select" className="border rounded-lg px-3 py-2 text-sm">
                  {OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <input value={form.cond_value} onChange={(e) => setForm({ ...form, cond_value: e.target.value })}
                  placeholder="value" data-testid="rule-cond-value-input" className="border rounded-lg px-3 py-2 text-sm" />
                <select value={form.action_type} onChange={(e) => setForm({ ...form, action_type: e.target.value })}
                  data-testid="rule-action-select" className="border rounded-lg px-3 py-2 text-sm">
                  {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <button type="submit" data-testid="rule-create-submit"
                className="inline-flex items-center gap-2 bg-indigo-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-indigo-700 transition-colors">
                <Plus className="w-4 h-4" /> Create Rule
              </button>
            </form>

            <div className="space-y-2" data-testid="workflow-automation-rules-list">
              <h2 className="font-semibold text-gray-900">Rules ({rules.length})</h2>
              {rules.length === 0 ? (
                <div className="text-center py-8 text-gray-400" data-testid="workflow-automation-empty">No rules yet.</div>
              ) : rules.map((r) => (
                <div key={r.id} data-testid={`rule-${r.id}`} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{r.name}</span>
                        <span data-testid={`rule-status-${r.id}`} className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${r.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {r.enabled ? 'enabled' : 'disabled'}
                        </span>
                        <span className="text-[10px] uppercase text-gray-400">{r.trigger_type}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{r.description || '—'}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {(r.conditions || []).map((c, i) => <span key={i}>{c.field} {c.operator} {String(c.value)}; </span>)}
                        → {r.action?.type}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <button onClick={() => evaluate(r)} data-testid={`rule-evaluate-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                        <Play className="w-4 h-4" /> Dry-run
                      </button>
                      <button onClick={() => toggle(r)} data-testid={`rule-toggle-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs text-gray-700 hover:underline">
                        <Power className="w-4 h-4" /> {r.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => remove(r)} data-testid={`rule-delete-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline">
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-testid="workflow-automation-eval-panel">
              <h2 className="font-semibold text-gray-900">Dry-Run Context</h2>
              <textarea value={evalContext} onChange={(e) => setEvalContext(e.target.value)} rows={4}
                data-testid="eval-context-input" className="border rounded-lg px-3 py-2 text-sm w-full font-mono" />
              {evalResult && (
                <div data-testid="eval-result" className={`rounded-lg border p-3 text-sm ${evalResult.matched ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <p className="font-medium" data-testid="eval-matched">Matched: {String(evalResult.matched)}</p>
                  <p className="text-gray-600">Proposed action: {evalResult.proposed_action?.type || 'none'}</p>
                  <p className="text-xs text-gray-400 mt-1">{evalResult.note}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WorkflowAutomationPage;
